import { useState, useCallback, useEffect } from 'react';
import {
  Upload,
  LayoutGrid,
  Grid2X2,
  Rows3,
  CheckSquare,
  Trash2,
  Tag,
  Globe,
  EyeOff,
  X,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { SplitPanelLayout } from '../components/layout/SplitPanelLayout';
import { usePhotos, useDeletePhoto, useAddPhotoTag, useUpdatePhoto } from '../hooks/usePhotos';
import { useUploadQueue } from '../hooks/useUploadQueue';
import { useEvents, useEventTypes } from '../hooks/useEvents';
import { useVenues } from '../hooks/useVenues';
import { useVendors } from '../hooks/useVendors';
import { useUsers } from '../hooks/useUsers';
import { useClientEvents } from '../hooks/usePortal';
import {
  PhotoGrid,
  PhotoDetailPanel,
  PhotoFilters,
  type PhotoFiltersState,
  PhotoUploadDropzone,
  GooglePhotosPicker,
  UploadProgressBar,
} from '../components/photos';
import { SystemRole } from '@peles/shared';
import type { PhotoTagEntityType } from '@peles/shared';
import type { PhotoGridSize } from '../components/photos/PhotoGrid';

type PanelState = { mode: 'closed' } | { mode: 'detail'; photoId: string } | { mode: 'upload' };

const PAGE_SIZE = 48;

export function PhotosPage() {
  const { user: currentUser } = useAuth();
  const isClient = currentUser?.systemRole === SystemRole.CLIENT;

  const [panel, setPanel] = useState<PanelState>({ mode: 'closed' });
  const [filters, setFilters] = useState<PhotoFiltersState>({
    search: '',
    entityType: '',
    publishStatus: '',
    eventTypeId: '',
  });
  const [page, setPage] = useState(1);
  const [gridSize, setGridSize] = useState<PhotoGridSize>('md');
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showBulkTagModal, setShowBulkTagModal] = useState(false);
  const [bulkTagEntityType, setBulkTagEntityType] = useState<string>('');
  const [bulkTagEntityId, setBulkTagEntityId] = useState<string>('');
  const [clientEventId, setClientEventId] = useState<string>('');

  const { data: clientEvents = [] } = useClientEvents();

  // Auto-select first event for client users
  useEffect(() => {
    if (isClient && clientEvents.length > 0 && !clientEventId) {
      setClientEventId(clientEvents[0].id);
    }
  }, [isClient, clientEvents, clientEventId]);

  const apiFilters: {
    caption?: string;
    entityType?: PhotoTagEntityType;
    entityId?: string;
    publishStatus?: string;
    page?: number;
    limit?: number;
  } = {
    page,
    limit: PAGE_SIZE,
  };
  if (filters.search) apiFilters.caption = filters.search;
  if (filters.publishStatus) apiFilters.publishStatus = filters.publishStatus;
  if (isClient) {
    // CLIENT users always see only their selected event's photos
    apiFilters.entityType = 'EVENT' as PhotoTagEntityType;
    if (clientEventId) apiFilters.entityId = clientEventId;
  } else if (filters.eventTypeId) {
    // Narrowing to a website section: the same entityType/entityId pair the
    // Type filter uses, which is why the two clear each other.
    apiFilters.entityType = 'EVENT_TYPE' as PhotoTagEntityType;
    apiFilters.entityId = filters.eventTypeId;
  } else if (filters.entityType) {
    apiFilters.entityType = filters.entityType as PhotoTagEntityType;
  }

  const { data, isLoading } = usePhotos(apiFilters);
  const deletePhoto = useDeletePhoto();
  const addPhotoTag = useAddPhotoTag();
  const updatePhoto = useUpdatePhoto();
  const { data: events = [] } = useEvents();
  const { data: venues = [] } = useVenues();
  const { data: eventTypes = [] } = useEventTypes();
  const { data: vendors = [] } = useVendors();
  const { data: users = [] } = useUsers();
  const { addFiles, waitForCapacity, queue, progress, isUploading, clear } = useUploadQueue(venues);

  // Publish state is narrowed by the query, so the page, the total and the
  // "load more" count all describe the same set. Filtering here instead meant
  // "Public Only" searched whichever forty-eight had already been fetched.
  const photos = data?.photos || [];
  const total = data?.total || 0;
  const hasMore = page * PAGE_SIZE < total;

  const handleSelectPhoto = useCallback((id: string) => {
    setPanel({ mode: 'detail', photoId: id });
  }, []);

  const handleClose = useCallback(() => {
    setPanel({ mode: 'closed' });
  }, []);

  const handleUploadClick = useCallback(() => {
    setPanel((prev) => (prev.mode === 'upload' ? { mode: 'closed' } : { mode: 'upload' }));
  }, []);

  const handleFilesSelected = useCallback(
    async (files: File[]) => {
      // Wait for the queue to have room before taking more. A drag-and-drop of
      // a folder returns at once; a Google Photos import of a few thousand is
      // held here between files, which is what keeps the backlog — and the
      // memory it occupies — bounded.
      await waitForCapacity();
      if (isClient && clientEventId) {
        addFiles(files, [{ entityType: 'EVENT' as PhotoTagEntityType, entityId: clientEventId }]);
      } else {
        addFiles(files);
      }
    },
    [addFiles, waitForCapacity, isClient, clientEventId],
  );

  const handleToggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleBulkDelete = useCallback(async () => {
    for (const id of selectedIds) {
      await deletePhoto.mutateAsync(id);
    }
    setSelectedIds(new Set());
    setMultiSelectMode(false);
  }, [selectedIds, deletePhoto]);

  /**
   * Publish (or unpublish) every selected photo in one go.
   *
   * `isPublic` is what the public gallery reads, so this is the bulk form of the
   * checkbox in the detail panel — ticking a hundred photographs one at a time
   * is the reason a season's work never reaches the site. Failures are skipped
   * rather than aborting the run: a photo that cannot be published should not
   * strand the ninety-nine behind it.
   */
  const handleBulkPublish = useCallback(
    async (isPublic: boolean) => {
      if (selectedIds.size === 0) return;
      for (const id of selectedIds) {
        try {
          // Unpublishing pulls it off the landing page too — a photo that is not
          // public cannot be featured, and leaving the flag set would republish
          // it the moment anyone ticked `isPublic` again.
          await updatePhoto.mutateAsync({
            id,
            updates: isPublic ? { isPublic: true } : { isPublic: false, showOnLanding: false },
          });
        } catch {
          // Skip the ones that fail; the rest of the selection still publishes.
        }
      }
      setSelectedIds(new Set());
      setMultiSelectMode(false);
    },
    [selectedIds, updatePhoto],
  );

  const handleBulkTag = useCallback(async () => {
    if (!bulkTagEntityType || !bulkTagEntityId || selectedIds.size === 0) return;
    for (const photoId of selectedIds) {
      try {
        await addPhotoTag.mutateAsync({
          photoId,
          tag: {
            entityType: bulkTagEntityType as PhotoTagEntityType,
            entityId: bulkTagEntityId,
          },
        });
      } catch {
        // Skip duplicates silently (unique constraint)
      }
    }
    setShowBulkTagModal(false);
    setBulkTagEntityType('');
    setBulkTagEntityId('');
    setSelectedIds(new Set());
    setMultiSelectMode(false);
  }, [bulkTagEntityType, bulkTagEntityId, selectedIds, addPhotoTag]);

  const bulkTagEntityOptions =
    bulkTagEntityType === 'EVENT_TYPE'
      ? eventTypes.map((t: { id: string; name: string }) => ({ id: t.id, label: t.name }))
      : bulkTagEntityType === 'EVENT'
        ? events.map((e: { id: string; name: string }) => ({ id: e.id, label: e.name }))
        : bulkTagEntityType === 'VENUE'
          ? venues.map((v: { id: string; name: string }) => ({ id: v.id, label: v.name }))
          : bulkTagEntityType === 'VENDOR'
            ? vendors.map((v) => ({ id: v.id, label: v.businessName }))
            : bulkTagEntityType === 'USER'
              ? users.map((u: { id: string; name: string }) => ({ id: u.id, label: u.name }))
              : [];

  const handleFiltersChange = useCallback((f: PhotoFiltersState) => {
    setFilters(f);
    setPage(1);
  }, []);

  if (!currentUser) return null;

  const panelOpen = panel.mode !== 'closed';
  const selectedPhotoId = panel.mode === 'detail' ? panel.photoId : null;

  return (
    <div>
      <div className="flex items-center justify-between mb-6 sticky top-0 z-10 bg-gray-50 py-3 -mt-3 -mx-1 px-1">
        <h1 className="text-2xl font-bold text-gray-900">
          Photos
          {total > 0 && <span className="ml-2 text-base font-normal text-gray-400">({total})</span>}
        </h1>
        <div className="flex items-center gap-2">
          {/* Grid size toggle */}
          <div className="flex items-center border border-gray-200 rounded-md overflow-hidden">
            <button
              onClick={() => setGridSize('sm')}
              className={`p-1.5 ${gridSize === 'sm' ? 'bg-primary-50 text-primary-600' : 'text-gray-500 hover:text-gray-700'}`}
              title="Small grid"
            >
              <LayoutGrid size={16} />
            </button>
            <button
              onClick={() => setGridSize('md')}
              className={`p-1.5 ${gridSize === 'md' ? 'bg-primary-50 text-primary-600' : 'text-gray-500 hover:text-gray-700'}`}
              title="Medium grid"
            >
              <Grid2X2 size={16} />
            </button>
            <button
              onClick={() => setGridSize('lg')}
              className={`p-1.5 ${gridSize === 'lg' ? 'bg-primary-50 text-primary-600' : 'text-gray-500 hover:text-gray-700'}`}
              title="Large grid"
            >
              <Rows3 size={16} />
            </button>
          </div>
          {/* Multi-select toggle — hidden for client users (read-only context) */}
          {!isClient && (
            <button
              onClick={() => {
                setMultiSelectMode((m) => !m);
                setSelectedIds(new Set());
              }}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm rounded-md border transition-colors ${
                multiSelectMode
                  ? 'bg-primary-50 border-primary-300 text-primary-700'
                  : 'border-gray-200 text-gray-600 hover:border-gray-300'
              }`}
            >
              <CheckSquare size={15} />
              <span className="hidden sm:inline">Select</span>
            </button>
          )}
          <GooglePhotosPicker onFilesSelected={handleFilesSelected} disabled={isUploading} />
          <button
            onClick={handleUploadClick}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700"
          >
            <Upload size={16} />
            <span className="hidden sm:inline">Upload Photos</span>
          </button>
        </div>
      </div>

      {/* Event selector for client users */}
      {isClient && clientEvents.length > 1 && (
        <div className="mb-4 flex items-center gap-3">
          <label className="text-sm font-medium text-gray-700">Event:</label>
          <select
            value={clientEventId}
            onChange={(e) => {
              setClientEventId(e.target.value);
              setPage(1);
            }}
            className="px-3 py-1.5 border border-gray-300 rounded-md text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-primary-500"
            data-testid="client-event-selector"
          >
            {clientEvents.map((ev) => (
              <option key={ev.id} value={ev.id}>
                {ev.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Bulk action bar — hidden for client users */}
      {!isClient && multiSelectMode && selectedIds.size > 0 && (
        <div className="flex items-center gap-3 mb-4 p-3 bg-primary-50 border border-primary-200 rounded-lg">
          <span className="text-sm text-primary-700 font-medium">{selectedIds.size} selected</span>
          <button
            onClick={handleBulkDelete}
            disabled={deletePhoto.isPending}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
          >
            <Trash2 size={14} />
            Delete
          </button>
          <button
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:border-gray-400 text-gray-700"
            onClick={() => setShowBulkTagModal(true)}
          >
            <Tag size={14} />
            Tag {selectedIds.size}
          </button>
          <button
            onClick={() => handleBulkPublish(true)}
            disabled={updatePhoto.isPending}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:border-gray-400 text-gray-700 disabled:opacity-50"
          >
            <Globe size={14} />
            Publish to website
          </button>
          <button
            onClick={() => handleBulkPublish(false)}
            disabled={updatePhoto.isPending}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:border-gray-400 text-gray-700 disabled:opacity-50"
          >
            <EyeOff size={14} />
            Unpublish
          </button>
          <button
            className="ml-auto text-sm text-gray-500 hover:text-gray-700"
            onClick={() => {
              setMultiSelectMode(false);
              setSelectedIds(new Set());
            }}
          >
            Cancel
          </button>
        </div>
      )}

      {!isClient && (
        <PhotoFilters
          filters={filters}
          onFiltersChange={handleFiltersChange}
          eventTypes={eventTypes}
        />
      )}
      <SplitPanelLayout
        panelOpen={panelOpen}
        onClosePanel={handleClose}
        listContent={
          <>
            {(panel.mode === 'upload' || isUploading || queue.length > 0) && (
              <div className="mb-4">
                <PhotoUploadDropzone onFilesSelected={handleFilesSelected} disabled={isUploading} />
                <UploadProgressBar
                  queue={queue}
                  progress={progress}
                  isUploading={isUploading}
                  onClear={clear}
                />
              </div>
            )}
            <PhotoGrid
              photos={photos}
              selectedPhotoId={selectedPhotoId}
              onSelectPhoto={handleSelectPhoto}
              isLoading={isLoading}
              size={gridSize}
              multiSelectMode={multiSelectMode}
              selectedIds={selectedIds}
              onToggleSelect={handleToggleSelect}
            />
            {hasMore && (
              <div className="mt-6 flex justify-center">
                <button
                  onClick={() => setPage((p) => p + 1)}
                  className="px-6 py-2 text-sm border border-gray-300 rounded-md hover:border-gray-400 text-gray-700"
                >
                  Load more ({total - page * PAGE_SIZE} remaining)
                </button>
              </div>
            )}
          </>
        }
        panelContent={
          panel.mode === 'detail' ? (
            <PhotoDetailPanel
              key={panel.photoId}
              photoId={panel.photoId}
              onClose={handleClose}
              currentUser={currentUser}
            />
          ) : null
        }
      />

      {/* Bulk Tag Modal */}
      {showBulkTagModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-gray-900">
                Tag {selectedIds.size} photos
              </h3>
              <button
                onClick={() => setShowBulkTagModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={16} />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tag Type</label>
                <select
                  value={bulkTagEntityType}
                  onChange={(e) => {
                    setBulkTagEntityType(e.target.value);
                    setBulkTagEntityId('');
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  data-testid="bulk-tag-type"
                >
                  <option value="">Select type...</option>
                  {/* First, because it is the one that puts a photo on the website. */}
                  <option value="EVENT_TYPE">Category (website section)</option>
                  <option value="EVENT">Event</option>
                  <option value="VENUE">Venue</option>
                  <option value="VENDOR">Vendor</option>
                  <option value="USER">User</option>
                </select>
              </div>
              {bulkTagEntityType && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Select{' '}
                    {bulkTagEntityType === 'EVENT_TYPE'
                      ? 'category'
                      : bulkTagEntityType.toLowerCase()}
                  </label>
                  <select
                    value={bulkTagEntityId}
                    onChange={(e) => setBulkTagEntityId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    data-testid="bulk-tag-entity"
                  >
                    <option value="">Select...</option>
                    {bulkTagEntityOptions.map((opt) => (
                      <option key={opt.id} value={opt.id}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setShowBulkTagModal(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkTag}
                disabled={!bulkTagEntityType || !bulkTagEntityId || addPhotoTag.isPending}
                className="px-4 py-2 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700 disabled:opacity-50"
                data-testid="bulk-tag-apply"
              >
                Apply Tag
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
